<template>
  <div class="tv-packages-container">
    <!-- Header & Filters -->
    <vx-card class="mb-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <!-- Left: Title & Add Button -->
        <div class="flex items-center gap-4">
          <h4 class="font-semibold text-lg">TV Packages</h4>
          <vs-button color="primary" icon="add" size="small" @click="openModal(null)">
            Add Package
          </vs-button>
        </div>

        <!-- Right: Filters & View Toggle -->
        <div class="flex flex-wrap items-center gap-3">
          <vs-input 
            v-model="filters.search" 
            placeholder="Search..." 
            icon="search"
            size="small"
            class="w-48"
          />
          <vs-select v-model="filters.status" size="small" class="w-32">
            <vs-select-item :value="null" text="All Status" />
            <vs-select-item :value="1" text="Active" />
            <vs-select-item :value="0" text="Inactive" />
          </vs-select>
          <vs-select v-model="filters.featured" size="small" class="w-32">
            <vs-select-item :value="null" text="All" />
            <vs-select-item :value="1" text="Featured" />
            <vs-select-item :value="0" text="Not Featured" />
          </vs-select>
          <div class="view-toggle flex border rounded overflow-hidden">
            <button :class="['px-3 py-1', viewMode === 'table' ? 'bg-primary text-white' : 'bg-gray-100']" @click="viewMode = 'table'">
              <feather-icon icon="ListIcon" svgClasses="w-4 h-4" />
            </button>
            <button :class="['px-3 py-1', viewMode === 'grid' ? 'bg-primary text-white' : 'bg-gray-100']" @click="viewMode = 'grid'">
              <feather-icon icon="GridIcon" svgClasses="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </vx-card>

    <!-- Add/Edit Dialog -->
    <vs-popup class="package-dialog" :title="pkg.id ? 'Edit Package' : 'Add New Package'" :active.sync="dialogActive">
      <div class="dialog-content">
        <div class="grid grid-cols-2 gap-4">
          <div class="col-span-2 md:col-span-1">
            <label class="block text-sm font-medium mb-1">Package Name <span class="text-danger">*</span></label>
            <vs-input v-model="pkg.name" placeholder="Enter package name" class="w-full" />
          </div>
          <div class="col-span-2 md:col-span-1">
            <label class="block text-sm font-medium mb-1">Arabic Name</label>
            <vs-input v-model="pkg.ar_name" placeholder="أدخل اسم الباقة" class="w-full" dir="rtl" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span class="text-sm font-medium">Active</span>
            <vs-switch v-model="pkg.status" color="success" />
          </div>
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span class="text-sm font-medium">Featured</span>
            <vs-switch v-model="pkg.featured" color="warning" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label class="block text-sm font-medium mb-1">Package Image</label>
            <div class="upload-box" @click="$refs.image.click()">
              <img v-if="pkg.image && typeof pkg.image === 'string'" :src="pkg.image" class="upload-preview" />
              <img v-else-if="imagePreview" :src="imagePreview" class="upload-preview" />
              <div v-else class="upload-placeholder">
                <feather-icon icon="UploadIcon" svgClasses="w-6 h-6 text-gray-400" />
                <span class="text-xs text-gray-400 mt-1">Upload Image</span>
              </div>
            </div>
            <input type="file" ref="image" class="hidden" accept="image/*" @change="handleImage()" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Secondary Image</label>
            <div class="upload-box" @click="$refs.secImage.click()">
              <img v-if="pkg.secondary_image && typeof pkg.secondary_image === 'string'" :src="pkg.secondary_image" class="upload-preview" />
              <img v-else-if="secImagePreview" :src="secImagePreview" class="upload-preview" />
              <div v-else class="upload-placeholder">
                <feather-icon icon="UploadIcon" svgClasses="w-6 h-6 text-gray-400" />
                <span class="text-xs text-gray-400 mt-1">Upload Image</span>
              </div>
            </div>
            <input type="file" ref="secImage" class="hidden" accept="image/*" @change="handleSecondaryImage()" />
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6 pt-4 border-t">
          <vs-button type="flat" @click="dialogActive = false">Cancel</vs-button>
          <vs-button color="primary" @click="savePackage" :disabled="!pkg.name">
            {{ pkg.id ? 'Update' : 'Create' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Table View -->
    <vx-card v-if="viewMode === 'table'">
      <vs-table :data="filteredPackages" pagination :max-items="10" search>
        <template slot="thead">
          <vs-th sort-key="id" width="60">ID</vs-th>
          <vs-th width="80">Image</vs-th>
          <vs-th sort-key="name">Name</vs-th>
          <vs-th sort-key="ar_name">Arabic Name</vs-th>
          <vs-th sort-key="status" width="100">Status</vs-th>
          <vs-th width="100">Featured</vs-th>
          <vs-th width="100">Channels</vs-th>
          <vs-th width="150">Actions</vs-th>
        </template>

        <template slot-scope="{data}">
          <tbody>
            <vs-tr v-for="(pack, index) in data" :key="index" :data="pack">
              <vs-td :data="pack.id">
                <div class="flex items-center gap-2">
                  {{ pack.id }}
                </div>
              </vs-td>
              <vs-td>
                <img v-if="pack.image" :src="pack.image" class="w-12 h-8 object-cover rounded" />
                <div v-else class="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
                  <feather-icon icon="ImageIcon" svgClasses="w-4 h-4 text-gray-300" />
                </div>
              </vs-td>
              <vs-td :data="pack.name">
                <span class="font-medium">{{ pack.name }}</span>
              </vs-td>
              <vs-td :data="pack.ar_name">
                <span dir="rtl">{{ pack.ar_name }}</span>
              </vs-td>
              <vs-td :data="pack.status">
                <vs-chip :color="pack.status ? 'success' : 'danger'" size="small">
                  {{ pack.status ? 'Active' : 'Inactive' }}
                </vs-chip>
              </vs-td>
              <vs-td>
                <vs-chip v-if="pack.featured" color="warning" size="small">Featured</vs-chip>
                <span v-else class="text-gray-400">-</span>
              </vs-td>
              <vs-td>
                <span class="font-medium">{{ pack.channels ? pack.channels.length : 0 }}</span>
              </vs-td>
              <vs-td>
                <div class="flex gap-1">
                  <vs-button type="flat" color="warning" icon="edit" size="small" @click="openModal(pack)" />
                  <vs-button type="flat" color="danger" icon="delete" size="small" @click="confirmDelete(pack)" />
                </div>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </vx-card>

    <!-- Grid View -->
    <div v-else>
      <draggable v-model="filteredPackages" handle=".grid-drag-handle" @end="updateSequence" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <vx-card v-for="pack in filteredPackages" :key="pack.id" class="package-card" :class="{ 'opacity-60': !pack.status }">
          <div class="grid-drag-handle absolute top-2 left-2 bg-white rounded p-1 shadow cursor-move opacity-0 hover:opacity-100 transition-opacity">
            <feather-icon icon="MoveIcon" svgClasses="w-4 h-4 text-gray-400" />
          </div>
          
          <div class="relative h-32 -mx-4 -mt-4 mb-3 overflow-hidden bg-gray-100">
            <img v-if="pack.image" :src="pack.image" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center">
              <feather-icon icon="PackageIcon" svgClasses="w-10 h-10 text-gray-300" />
            </div>
            <div class="absolute top-2 right-2 flex flex-col gap-1">
              <vs-chip :color="pack.status ? 'success' : 'danger'" size="small">
                {{ pack.status ? 'Active' : 'Inactive' }}
              </vs-chip>
              <vs-chip v-if="pack.featured" color="warning" size="small">Featured</vs-chip>
            </div>
          </div>

          <h5 class="font-semibold text-base mb-1">{{ pack.name }}</h5>
          <p class="text-sm text-gray-500 mb-2" dir="rtl">{{ pack.ar_name || '-' }}</p>
          
          <div class="flex items-center justify-between pt-3 border-t">
            <span class="text-sm text-gray-500">
              <feather-icon icon="TvIcon" svgClasses="w-4 h-4 inline mr-1" />
              {{ pack.channels ? pack.channels.length : 0 }} channels
            </span>
            <div class="flex gap-1">
              <vs-button type="flat" color="warning" icon="edit" size="small" @click="openModal(pack)" />
              <vs-button type="flat" color="danger" icon="delete" size="small" @click="confirmDelete(pack)" />
            </div>
          </div>
        </vx-card>
      </draggable>
    </div>

    <!-- Empty State -->
    <vx-card v-if="filteredPackages.length === 0" class="text-center py-10">
      <feather-icon icon="PackageIcon" svgClasses="w-16 h-16 text-gray-300 mx-auto" />
      <h4 class="text-lg text-gray-500 mt-4">No packages found</h4>
      <p class="text-gray-400 mb-4">Create your first TV package to get started</p>
      <vs-button color="primary" @click="openModal(null)">Add Package</vs-button>
    </vx-card>
  </div>
</template>

<script>
/* eslint-disable */
import draggable from "vuedraggable";

export default {
  components: { draggable },

  data() {
    return {
      dialogActive: false,
      viewMode: 'table',
      packages: [],
      imagePreview: null,
      secImagePreview: null,
      filters: {
        search: '',
        status: null,
        featured: null
      },
      pkg: {
        id: 0,
        name: "",
        ar_name: "",
        image: "",
        secondary_image: "",
        featured: false,
        status: true,
      },
    };
  },

  created() {
    this.fetchPackages();
  },

  computed: {
    storePackages() {
      return this.$store.state.Tv.packages;
    },
    filteredPackages() {
      let result = [...this.packages];

      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        result = result.filter(pkg => 
          (pkg.name && pkg.name.toLowerCase().includes(search)) || 
          (pkg.ar_name && pkg.ar_name.toLowerCase().includes(search))
        );
      }

      if (this.filters.status !== null) {
        result = result.filter(pkg => pkg.status === this.filters.status);
      }

      if (this.filters.featured !== null) {
        result = result.filter(pkg => pkg.featured === this.filters.featured);
      }

      return result;
    }
  },

  watch: {
    storePackages: {
      handler(newVal) {
        this.packages = newVal || [];
      },
      deep: true
    }
  },

  methods: {
    fetchPackages() {
      this.$store.dispatch("Tv/fetchPackages", { page: 1 });
      setTimeout(() => {
        this.packages = this.$store.state.Tv.packages || [];
      }, 300);
    },

    openModal(pack) {
      this.imagePreview = null;
      this.secImagePreview = null;

      if (pack) {
        this.pkg = { ...pack };
      } else {
        this.pkg = {
          id: 0,
          name: "",
          ar_name: "",
          image: "",
          secondary_image: "",
          featured: false,
          status: true,
        };
      }
      this.dialogActive = true;
    },

    async savePackage() {
      if (!this.pkg.name) {
        this.$vs.notify({ color: 'warning', title: 'Warning', text: 'Please enter package name' });
        return;
      }

      this.$vs.loading();
      const data = new FormData();
      data.append('id', this.pkg.id);
      data.append('name', this.pkg.name);
      data.append('ar_name', this.pkg.ar_name || '');
      data.append('featured', this.pkg.featured ? 1 : 0);
      data.append('status', this.pkg.status ? 1 : 0);

      if (this.pkg.image && typeof this.pkg.image !== 'string') {
        data.append('image', this.pkg.image);
      }
      if (this.pkg.secondary_image && typeof this.pkg.secondary_image !== 'string') {
        data.append('secondary_image', this.pkg.secondary_image);
      }

      try {
        await this.axios.post(`${this.$apiUrl}/v2/tv/packages`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        this.$vs.loading.close();
        this.$vs.notify({ color: 'success', title: 'Package Saved', text: '' });
        this.dialogActive = false;
        this.fetchPackages();
      } catch (err) {
        this.$vs.loading.close();
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to save package' });
      }
    },

    updateSequence() {
      const ids = this.filteredPackages.map(p => p.id);
      this.$store.dispatch("Tv/updatePackagesSequence", { data: ids });
    },

    confirmDelete(pack) {
      this.pkg = pack;
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete Package',
        text: `Are you sure you want to delete "${pack.name}"?`,
        accept: this.deletePackage
      });
    },

    async deletePackage() {
      try {
        await this.axios.post(`${this.$apiUrl}/v2/tv/packages/${this.pkg.id}/del`);
        this.$vs.notify({ color: 'success', title: 'Package Deleted', text: '' });
        this.fetchPackages();
      } catch (err) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Failed to delete package' });
      }
    },

    handleImage() {
      const file = this.$refs.image.files[0];
      if (file) {
        this.pkg.image = file;
        this.imagePreview = URL.createObjectURL(file);
      }
    },

    handleSecondaryImage() {
      const file = this.$refs.secImage.files[0];
      if (file) {
        this.pkg.secondary_image = file;
        this.secImagePreview = URL.createObjectURL(file);
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.tv-packages-container {
  .view-toggle button {
    transition: all 0.2s;
  }

  .upload-box {
    border: 2px dashed #e2e8f0;
    border-radius: 0.5rem;
    height: 100px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    overflow: hidden;

    &:hover {
      border-color: #7367f0;
      background: #f8f8ff;
    }

    .upload-preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  .package-card {
    position: relative;
    transition: all 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

      .grid-drag-handle {
        opacity: 1;
      }
    }
  }

  .drag-handle {
    opacity: 0.5;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
}

.package-dialog {
  .vs-popup {
    max-width: 600px;
    width: 90%;
  }
}
</style>
